import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shield } from "lucide-react";
import JeaButton from "@/components/jea/JeaButton";
import { TIERS, TIER_MONTHS } from "@/lib/pricing";
import { cn } from "@/lib/utils";

const tierBadgeStyle = (tier) => {
  if (tier === "gold") return { background: "var(--color-tier-gold-bg-15)", border: "1px solid var(--color-tier-gold-border-40)", color: "var(--color-tier-gold)" };
  if (tier === "silver") return { background: "rgba(192, 192, 192, 0.15)", border: "1px solid rgba(192, 192, 192, 0.40)", color: "var(--color-tier-silver)" };
  return { background: "rgba(205, 127, 50, 0.15)", border: "1px solid rgba(205, 127, 50, 0.40)", color: "var(--color-tier-bronze)" };
};

const TierBadge = ({ tier }) => {
  const info = TIERS.find((t) => t.key === tier) || TIERS[0];
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full" style={tierBadgeStyle(tier)}>
      <Shield size={14} strokeWidth={2.5} />
      <span className="text-[11px] font-bold tracking-[0.1em]">{info.label}</span>
    </div>
  );
};

const TierChangeModal = ({ open, currentTier, onCancel, onConfirm, submitting }) => {
  const [selected, setSelected] = React.useState(currentTier || "bronze");
  React.useEffect(() => {
    if (open) setSelected(currentTier || "bronze");
  }, [open, currentTier]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-[12px] z-[100]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onCancel}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-[101] flex items-center justify-center px-4"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            data-testid="tier-change-modal"
          >
            <div className="w-full max-w-[480px] bg-bg-secondary border border-border-default rounded-[16px] p-10 shadow-elevated">
              <h3 className="text-white font-bold text-[18px] mb-1">Change Tier</h3>
              <p className="text-text-secondary text-[13px] mb-6">
                Choose a new query frequency. The next query date will be recomputed from today.
              </p>

              <div className="grid grid-cols-3 gap-2 mb-6" data-testid="tier-change-picker">
                {TIERS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setSelected(t.key)}
                    className={cn(
                      "flex flex-col items-center gap-1 py-3 rounded-[8px] border transition-all",
                      selected === t.key
                        ? "bg-bg-primary border-accent-pink shadow-pink-glow"
                        : "bg-bg-primary border-border-default hover:border-accent-pink/60"
                    )}
                    data-testid={`tier-change-option-${t.key}`}
                  >
                    <Shield size={20} style={{ color: t.color }} />
                    <span className="text-[11px] font-bold tracking-[0.1em]" style={{ color: t.color }}>
                      {t.label}
                    </span>
                  </button>
                ))}
              </div>

              <div className="flex items-center justify-center gap-3 mb-2">
                <TierBadge tier={currentTier} />
                <span className="text-text-secondary">→</span>
                <TierBadge tier={selected} />
              </div>
              <p className="text-[13px] text-text-secondary text-center mb-8" data-testid="tier-change-summary">
                From every {TIER_MONTHS[currentTier]} months to every {TIER_MONTHS[selected]} months
              </p>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onCancel}
                  className="flex-1 h-11 rounded-[8px] border border-border-default text-text-secondary hover:text-white hover:bg-bg-tertiary transition-colors text-[14px] font-semibold"
                  data-testid="tier-change-cancel"
                >
                  Cancel
                </button>
                <JeaButton
                  onClick={() => onConfirm(selected)}
                  loading={submitting}
                  className="flex-1"
                  data-testid="tier-change-confirm"
                >
                  Confirm Change
                </JeaButton>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default TierChangeModal;
